import {
    BankFinancialReviewDetailViewModel,
    BankFinancialReviewLineItem,
    FinancialReviewLandingRow,
    ReviewModuleType,
} from '../models/bank-financial-review.models';

export class BankFinancialReviewMapperService {
  mapLandingRows(payload: unknown): FinancialReviewLandingRow[] {
    if (!Array.isArray(payload)) {
      return [];
    }

    return payload.map((row): FinancialReviewLandingRow => {
      const raw = (row ?? {}) as Record<string, unknown>;
      const lenderId =
        this.toStringValue(raw['lenderId'])
        || this.toStringValue(raw['lndrId'])
        || this.toStringValue(raw['lender_id'])
        || this.toStringValue(raw['lndr_id'])
        || '';
      const lenderName =
        this.toStringValue(raw['lenderName'])
        || this.toStringValue(raw['lenderDisplayName'])
        || this.toStringValue(raw['lndrNme'])
        || this.toStringValue(raw['lndr_nm'])
        || this.toStringValue(raw['lender_name'])
        || '';
      const financialPeriod = this.toStringValue(raw['financialPeriod']) || this.toStringValue(raw['businessQuarter']) || '';
      const statusLabel = this.toNullableString(raw['statusLabel']) || this.toNullableString(raw['workflowStatusLabel']);
      const workflowStatusId = this.toNullableNumber(raw['workflowStatusId']) ?? null;
      const workflowStatusCode =
        this.toNullableString(raw['workflowStatusCode']) ||
        this.toNullableString(raw['workflowStatus']) ||
        this.toNullableString(raw['lndrFnclSmssWkflStatVal']);
      const workflowStatusStyle =
        this.toNullableString(raw['workflowStatusStyle']) ||
        this.toNullableString(raw['statusStyle']);
      const reviewerDisplayName =
        this.toNullableString(raw['lndrFnclSmssLndrPrprUsrNme']) ||
        this.toNullableString(raw['reviewerDisplayName']) ||
        this.toNullableString(raw['reviewer']);
      const approverDisplayName =
        this.toNullableString(raw['lndrFnclSmssFnmAnlUsrNme']) ||
        this.toNullableString(raw['approverDisplayName']) ||
        this.toNullableString(raw['approver']);
      const rawModuleType = this.toStringValue(raw['moduleType']);
      const moduleType: ReviewModuleType =
        rawModuleType === 'NON_BANK' ? 'NON_BANK' :
        rawModuleType === 'BANK' ? 'BANK' :
        null;

      return {
        lenderId,
        lenderName,
        financialPeriod,
        statusLabel,
        workflowStatusId,
        workflowStatusCode,
        workflowStatusStyle,
        reviewerDisplayName,
        approverDisplayName,
        moduleType,
      };
    });
  }

  mapDetail(
    payload: unknown,
    _lenderId: string,
    financialPeriod: string,
  ): BankFinancialReviewDetailViewModel {
    const raw = (payload ?? {}) as Record<string, unknown>;
    const headerRaw = (raw['header'] ?? {}) as Record<string, unknown>;
    const mappedSections = this.mapDetailSections(raw['sections']);

    const resolvedFinancialPeriod =
      this.toStringValue(raw['financialPeriod']) ||
      this.toStringValue(headerRaw['financialPeriod']) ||
      financialPeriod;

    const lenderSubmitterDto = (raw['lenderSubmitter'] ?? null) as Record<string, unknown> | null;
    const analystDto = (raw['fannieMaeAnalyst'] ?? null) as Record<string, unknown> | null;
    const lenderPreparerName = this.toNullableString(raw['lndrFnclSmssLndrPrprUsrNme']);
    const fannieMaeAnalystName = this.toNullableString(raw['lndrFnclSmssFnmAnlUsrNme']);

    return {
      header: {
        financialPeriod: resolvedFinancialPeriod,
        financialQuarterSubmitted:
          this.toNullableString(raw['lndrFscQtrNo']) ||
          this.toNullableString(headerRaw['financialQuarterSubmitted']),
        lenderName:
          this.toNullableString(raw['lenderName']) ||
          this.toNullableString(raw['lenderDisplayName']) ||
          this.toNullableString(headerRaw['lenderName']),
        lenderSubmitter: lenderPreparerName ?? (lenderSubmitterDto
          ? (this.toNullableString(lenderSubmitterDto['displayName']) ?? this.toNullableString(lenderSubmitterDto['userId']))
          : this.toNullableString(headerRaw['lenderSubmitter'])),
        fannieMaeAnalyst: fannieMaeAnalystName ?? (analystDto
          ? (this.toNullableString(analystDto['displayName']) ?? this.toNullableString(analystDto['userId']))
          : this.toNullableString(headerRaw['fannieMaeAnalyst'])),
        statusLabel:
          this.toNullableString(raw['workflowStatusLabel']) ||
          this.toNullableString(raw['workflowStatus']) ||
          this.toNullableString(headerRaw['statusLabel']),
        workflowStatusId:
          this.toNullableNumber(raw['workflowStatusId']) ??
          this.toNullableNumber(headerRaw['workflowStatusId']) ??
          null,
        workflowStatusCode:
          this.toNullableString(raw['workflowStatusCode']) ||
          this.toNullableString(raw['lndrFnclSmssWkflStatVal']) ||
          this.toNullableString(raw['workflowStatus']) ||
          this.toNullableString(headerRaw['workflowStatusCode']),
        workflowStatusStyle:
          this.toNullableString(raw['workflowStatusStyle']) ||
          this.toNullableString(headerRaw['workflowStatusStyle']),
      },
      lockStatus: this.toNullableString(raw['lockStatus']),
      sections: mappedSections.sections,
      sectionOrderByKey: mappedSections.sectionOrderByKey,
      phase1Simulation: {
        saveSubmitPersistenceSimulated: false,
        workflowTransitionsSimulated: false,
      },
    };
  }

  mapDetailSections(sectionsInput: unknown): {
    sections: Record<string, BankFinancialReviewLineItem[]>;
    sectionOrderByKey: Record<string, number | null>;
  } {
    const mapped: Record<string, BankFinancialReviewLineItem[]> = {};
    const sectionOrderByKey: Record<string, number | null> = {};

    if (Array.isArray(sectionsInput)) {
      sectionsInput.forEach((section) => {
        const sectionRaw = (section ?? {}) as Record<string, unknown>;
        const sectionKey = this.normalizeSectionKey(
          this.toStringValue(sectionRaw['sectionKey'])
          || this.toStringValue(sectionRaw['sectionLabel'])
          || this.toStringValue(sectionRaw['sectionName'])
        );
        if (!sectionKey) {
          return;
        }
        sectionOrderByKey[sectionKey] = this.toNullableNumber(sectionRaw['sectionOrder']) ?? null;
        const rows = Array.isArray(sectionRaw['rows']) ? sectionRaw['rows'] : [];
        mapped[sectionKey] = rows.map((item) => {
          const raw = (item ?? {}) as Record<string, unknown>;
          const numericValue = raw['finalizedValue'];
          const sourceMetadata = (raw['sourceMetadata'] ?? null) as Record<string, unknown> | null;
          const overrideHistory = Array.isArray(raw['overrideHistory'])
            ? (raw['overrideHistory'] as Array<Record<string, unknown>>).map((entry) => ({
              overrideId: this.toNullableNumber(entry['overrideId']),
              overrideType: this.toNullableString(entry['overrideType']),
              overrideValue: this.toSourceValue(entry['overrideValue']),
              reason: this.toNullableString(entry['reason']),
              userId: this.toNullableString(entry['userId']),
              userDisplayName: this.toNullableString(entry['userDisplayName']),
              timestamp: this.toNullableString(entry['timestamp']),
            }))
            : [];
          const hasOverrideApplied = raw['lenderOverrideValue'] != null || raw['analystOverrideValue'] != null;
          return {
            lineValueId: this.toNullableNumber(raw['lineValueId']),
            applFieldSrcMapgId: this.toNullableNumber(raw['applFieldSrcMapgId']),
            sectionKey,
            fieldOrder: this.toNullableNumber(raw['fieldOrder']) ?? null,
            displayName:
              this.toStringValue(raw['lineItemName'])
              || this.toStringValue(raw['lineItmNme'])
              || this.toStringValue(raw['displayName'])
              || '',
            regulatoryLineItemNumber:
              this.toNullableString(raw['lineItemNumber'])
              || this.toNullableString(raw['lineItmNo'])
              || this.toNullableString(raw['regulatoryLineItemNumber']),
            valueDisplay: numericValue != null ? String(numericValue) : null,
            fiscalYtdValue: this.toSourceValue(raw['fiscalYtdValue']),
            sourceValue: this.toSourceValue(raw['sourceValue']),
            valueDataType: this.toValueDataType(raw['valueFormatType'] ?? raw['valueDataType']),
            approved: Boolean(raw['approved']),
            hasOverride: hasOverrideApplied || Boolean(raw['hasOverride']),
            hasOverrideApplied,
            sourceSystemName:
              this.toNullableString(sourceMetadata?.['sourceSystemName'])
              || this.toNullableString(raw['sourceSystemName'])
              || this.toNullableString(raw['srcSysNme']),
            sourceFieldName:
              this.toNullableString(sourceMetadata?.['sourceFieldName'])
              || this.toNullableString(raw['sourceFieldName'])
              || this.toNullableString(raw['srcFieldNme']),
            sourceDetails:
              this.toNullableString(sourceMetadata?.['sourceDetails'])
              || this.toNullableString(raw['sourceDetails']),
            overrideHistory,
          };
        });
      });
      return { sections: mapped, sectionOrderByKey };
    }

    const sectionsRaw = (sectionsInput ?? {}) as Record<string, unknown>;
    Object.entries(sectionsRaw).forEach(([sectionKey, items]) => {
      sectionOrderByKey[sectionKey] = null;
      if (!Array.isArray(items)) {
        mapped[sectionKey] = [];
        return;
      }
      mapped[sectionKey] = items.map((item) => {
        const raw = (item ?? {}) as Record<string, unknown>;
        return {
          lineValueId: this.toNullableNumber(raw['lineValueId']),
          applFieldSrcMapgId: this.toNullableNumber(raw['applFieldSrcMapgId']),
          sectionKey,
          fieldOrder: this.toNullableNumber(raw['fieldOrder']) ?? null,
          displayName: this.toStringValue(raw['displayName']) || this.toStringValue(raw['lineItemName']) || '',
          regulatoryLineItemNumber: this.toNullableString(raw['regulatoryLineItemNumber']) || this.toNullableString(raw['lineItemNumber']),
          valueDisplay: this.toNullableString(raw['finalizedValue']),
          fiscalYtdValue: this.toSourceValue(raw['fiscalYtdValue']),
          sourceValue: this.toSourceValue(raw['sourceValue']),
          valueDataType: this.toValueDataType(raw['valueDataType']),
          approved: Boolean(raw['approved']),
          hasOverride: Boolean(raw['hasOverride']),
          hasOverrideApplied: Boolean(raw['hasOverrideApplied'] ?? raw['hasOverride']),
          sourceSystemName: this.toNullableString(raw['sourceSystemName']),
          sourceFieldName: this.toNullableString(raw['sourceFieldName']),
          sourceDetails: this.toNullableString(raw['sourceDetails']),
        };
      });
    });
    return { sections: mapped, sectionOrderByKey };
  }

  unwrapChassisEnvelope(payload: unknown): unknown {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return payload;
    }

    const record = payload as Record<string, unknown>;
    return Object.prototype.hasOwnProperty.call(record, 'responsePayload')
      ? record['responsePayload']
      : payload;
  }

  private normalizeSectionKey(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    return trimmed
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  private toStringValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  }

  private toNullableString(value: unknown): string | null {
    const normalized = this.toStringValue(value).trim();
    return normalized.length > 0 ? normalized : null;
  }

  private toSourceValue(value: unknown): string | number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized.length > 0 ? normalized : null;
    }
    return null;
  }

  private toValueDataType(value: unknown): 'CURRENCY' | 'PERCENT' | 'RATIO' | 'TEXT' | 'TBD' {
    const candidate = this.toStringValue(value).toUpperCase();
    if (candidate === 'CURRENCY' || candidate === 'PERCENT' || candidate === 'RATIO' || candidate === 'TEXT') {
      return candidate;
    }
    return 'TBD';
  }

  private toNullableNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return undefined;
      }
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }
}
